import React from 'react';
import { AlertCircle } from 'lucide-react';

/**
 * Reusable form field component with error handling
 * @param {Object} props
 * @param {string} props.label - Field label
 * @param {string} props.name - Field name
 * @param {string} props.type - Input type
 * @param {string} props.value - Field value
 * @param {Function} props.onChange - Change handler
 * @param {string} props.placeholder - Placeholder text
 * @param {boolean} props.required - Whether field is required
 * @param {string} props.error - Error message
 * @param {React.ReactNode} props.icon - Left icon
 * @param {React.ReactNode} props.rightIcon - Right icon (for password toggle)
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element}
 */
export const FormField = ({ 
  label,
  name,
  type = 'text',
  value,
  onChange,
  placeholder,
  required = false,
  error,
  icon,
  rightIcon,
  className = '',
  ...props
}) => {
  const getFieldClasses = () => {
    const baseClasses = `w-full pr-4 py-4 border rounded-xl transition-all duration-200 bg-white ${icon ? 'pl-12' : 'pl-4'}`;
    
    if (error) {
      return `${baseClasses} border-error-300 focus:ring-2 focus:ring-error-500 focus:border-error-500`;
    }
    
    return `${baseClasses} border-gray-200 focus:ring-2 focus:ring-primary-500 focus:border-primary-500`;
  };

  // Check if this is a password field (to exclude error icon)
  const isPasswordField = name === 'password' || name === 'password_confirmation';

  return (
    <div className={`space-y-2 ${className}`}>
      {label && (
        <label htmlFor={name} className="block text-sm font-medium text-gray-700">
          {label}
        </label>
      )}
      <div className="relative">
        {/* Left Icon */}
        {icon && (
          <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400">
            {icon}
          </div>
        )}
        
        {/* Input Field */}
        <input
          id={name}
          name={name}
          type={type}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          required={required}
          className={getFieldClasses()}
          {...props}
        />
        
        {/* Right Icon (Error or Password Toggle) */}
        {(error || rightIcon) && (
          <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
            {error && !isPasswordField ?(
              <AlertCircle className="h-5 w-5 text-error-500" />
            ) : (
              rightIcon
            )}
          </div>
        )}
      </div>
      
      {/* Error Message */}
      {error && (
        <p className="text-sm text-error-600 mt-1 flex items-center">
          <AlertCircle className="h-4 w-4 mr-1" />
          {error}
        </p>
      )}
    </div>
  );
}; 